import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  AppBar,
  Tabs,
  Tab,
  IconButton,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Select,
  Checkbox,
  Chip,
  ListItemText,
  OutlinedInput,
  InputLabel,
  MenuItem,
  Paper,
  TextField,
  RadioGroup,
  Radio,
  FormLabel,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import Button from '../Button';
import TabPanel from '../../components/TabPanel';
import PhoneInput from '../../components/PhoneInput';
import Avatar from '../../components/Avatar';

import * as api from '../../services/api/admin';
import {fetchAllProvidedUrls} from '../../services/api/admin.js'
import { isEmailAvailable } from '../../services/api';
import data from './data.js';

import { useSnackbar } from 'notistack';
import { standardizePhoneNumber } from '../../util/standardizePhoneNumber';
import * as EmailValidator from 'email-validator';
import './style.css';
import { faxPattern, formatFax } from '../../util/formatFax.js';

const validatePhone = num => {
  return num.replace(/[a-z\W]/gi, '').length === 10 && !/[a-z]/i.test(num);
};

const splitUrl = url => {
  const splitUrl = url && url.split('/');
  return splitUrl && splitUrl[1];
};

function ProviderEditModal(props) {
  const {
    onClose,
    open,
    provider,
    saveTenancies,
    saveProfile,
    admin,
    classes: { modal, modalContent, actions, input, error, addSaveTenancy },
  } = props;
  const { enqueueSnackbar } = useSnackbar();
  const [tabPosition, setTabPosition] = useState(0);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [smsOptIn, setSmsOptIn] = useState(!provider.smsOptOut);
  const [firstName, setFirstName] = useState(provider.firstName || '');
  const [lastName, setLastName] = useState(provider.lastName || '');
  const [email, setEmail] = useState(provider.email || '');
  const [fax, setFax] = useState(provider.fax || '');
  const [notificationPreference, setNotificationPreference] = useState(
    provider.notificationPreference || 'email'
  );
  const [phone, setPhone] = useState(
    standardizePhoneNumber(provider.phone) || ''
  );
  const [smsPhone, setSmsPhone] = useState(
    standardizePhoneNumber(provider.phoneForSms) || ''
  );
  const [title, setTitle] = useState(provider.title || '');
  const [bio, setBio] = useState(provider.bio || '');
  const [profileUrl, setProfileUrl] = useState(provider.profileUrl || '');
  const [homepageUrl, setHomepageUrl] = useState(provider.homepageUrl || '');
  const [preferredAge, setPreferredAge] = useState(provider.preferredAge || '');
  const [preferredGender, setPreferredGender] = useState(
    provider.preferredGender || ''
  );
  const [degree, setDegree] = useState(provider.degree || '');
  const [education, setEducation] = useState(provider.education || '');
  const [providerSpecialtyIds, setProviderSpecialtyIds] = useState(
    (provider.specialties || []).map((s) => s.id)
  );

  const [providerUrl, setProviderUrl] = useState(
    splitUrl(provider.providerUrl) || ''
  );

  const [providerInsuranceIds, setProviderInsuranceIds] = useState(
    (provider.insurances || []).map((i) => i.id)
  );
  const [practiceId, setPracticeId] = useState(
    provider.practice && provider.practice.id ? provider.practice.id : ''
  );
  const [tenancies, setTenancies] = useState(
    (provider.tenancies || [])
      .map((tenancy) => ({
        id: tenancy.id,
        providerId: provider.id,
        location: tenancy.location || '',
        locationShortName: tenancy.locationShortName || '',
        color: tenancy.color || '',
        active: tenancy.active || false,
        apptDurationMinutes: tenancy.apptDurationMinutes || '',
        zipcode: tenancy.zipcode || '',
        virtualRoomUrl: tenancy.virtualRoomUrl || '',
        googleReviewLink: tenancy.googleReviewLink || '',
      }))
      .sort((t1, t2) => (t1.active === t2.active ? 0 : t1.active ? 1 : -1))
  );
  const [specialties, setSpecialties] = useState([]);
  const [insurances, setInsurances] = useState([]);
  const [practices, setPractices] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [allProvidedUrls,setAllProvidedUrls]= useState([]);

  useEffect(() => {
    (async () => {
      try {
        const fetchedSpecialties = await api.fetchSpecialties();
        const fetchedInsurances = await api.fetchInsurances();
        setSpecialties(fetchedSpecialties);
        setInsurances(fetchedInsurances);
        const getUrls = async () => {
          const urls = await fetchAllProvidedUrls();
          setAllProvidedUrls(urls);
        };
        getUrls();
        if (admin) {
          const fetchedPractices = await api.fetchPractices();
          setPractices(fetchedPractices);
        }
      } catch (err) {
        enqueueSnackbar(err.message || 'Oops something went wrong.');
      }
    })();
  }, []);

  const onTenancyFieldChange = idx => event =>
    setTenancies(
      tenancies.map((tenancy, i) => {
        if (i === idx)
          return { ...tenancy, [event.target.name]: event.target.value };
        return tenancy;
      })
    );

  const onNewTenancy = () => {
    const newTenancy = {
      providerId: provider.id,
      tenancy: '',
      locationShortName: '',
      zipcode: '',
      color: '',
      apptDurationMinutes: '',
      virtualRoomUrl: '',
    };
    setTenancies([...tenancies, newTenancy]);
  };

  const submitSave = () => {
    setButtonDisable(true);
    saveProfile({
      ...provider,
      tenancies,
      providerSpecialtyIds,
      providerInsuranceIds,
      practiceId,
      firstName: firstName.replace(/^\s+|\s+$/gm, ''),
      lastName: lastName.replace(/^\s+|\s+$/gm, ''),
      email,
      fax,
      phone,
      title,
      bio,
      profileUrl,
      providerUrl,
      homepageUrl,
      degree: degree.replace(/^\s+|\s+$/gm, ''),
      education,
      smsOptOut: !smsOptIn,
      phoneForSms: smsPhone,
      preferredAge,
      preferredGender,
      notificationPreference,
    });
  };

  const validateProvider = () => {
    let valid = true;
    const nextState = {};
    if (!firstName || !firstName.replace(/^\s+|\s+$/gm, '')) {
      valid = false;
      nextState.firstName = 'Required';
    }
    if (!lastName || !lastName.replace(/^\s+|\s+$/gm, '')) {
      valid = false;
      nextState.lastName = 'Required';
    }
    if (!bio || !bio.replace(/^\s+|\s+$/gm, '')) {
      valid = false;
      nextState.bio = 'Required';
    }
    if (!degree || !degree.replace(/^\s+|\s+$/gm, '')) {
      valid = false;
      nextState.degree = 'Required';
    }
    if (!providerUrl) {
      valid = false;
      nextState.providerUrl = 'Required';
    }
    if (!practiceId) {
      valid = false;
      nextState.practiceId = 'Required';
    }
    if (!email) {
      valid = false;
      nextState.email = 'Required';
    } else if (!EmailValidator.validate(email)) {
      valid = false;
      nextState.email = 'Must be a valid email';
    }
    if (phone && !validatePhone(phone)) {
      valid = false;
      nextState.phone = 'Must be a valid phone number';
    }
    if (!preferredAge) {
      valid = false;
      nextState.preferredAge = 'Required';
    }
    if (!preferredGender) {
      valid = false;
      nextState.preferredGender = 'Required';
    }
    if (smsOptIn && !smsPhone) {
      valid = false;
      nextState.smsPhone = 'Required';
    } else if (smsOptIn && !validatePhone(smsPhone)) {
      valid = false;
      nextState.smsPhone = 'Must be a valid phone number';
    }
    if (admin && !practiceId) {
      valid = false;
      nextState.practiceId = 'Required';
    }
    
    if (notificationPreference !== 'email' || (fax && fax.trim().length > 0)) {
      if (!fax) {
        valid = false;
        nextState.fax = 'Fax number is required';
      } else if (!faxPattern.test(fax)) {
        valid = false;
        nextState.fax = 'Enter a valid 10 digit fax number';
      }
    }

    setValidationErrors(nextState);
    return valid;
  };

  return (
    <Dialog fullWidth open={open} onClose={onClose} classes={{ paper: modal }}>
      <DialogTitle>
        <Grid container alignItems="center">
          {provider.id && (
            <Grid item xs={5} lg={3}>
              <Avatar
                providerId={provider.id}
                customStyles={{
                  width: '100px',
                  height: '100px',
                  marginLeft: '0px',
                }}
                className="profAvatar"
              />
            </Grid>
          )}

          <Grid
            item
            xs={provider.id ? 6 : 11}
            lg={provider.id ? 8 : 11}
            className="practiceEdit"
          >
            <h2>{provider.name || 'New Provider'}</h2>
          </Grid>

          <Grid item xs={1}>
            <IconButton aria-label="Close" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>

      <DialogContent classes={{ root: modalContent }}>
        <AppBar position="static" color="default">
          <Tabs
            value={tabPosition}
            onChange={(_, nextTabIndex) => setTabPosition(nextTabIndex)}
          >
            <Tab label="Profile" />
            <Tab label="Tenancies" />
          </Tabs>
        </AppBar>

        {tabPosition === 0 && (
          <div className="labelTxt">
            <TabPanel>
              <TextField
                fullWidth
                variant="outlined"
                label="First Name"
                name="firstName"
                value={firstName}
                onChange={event => {
                  setFirstName(event.target.value);
                  setValidationErrors({
                    ...validationErrors,
                    [event.target.name]: false,
                  });
                }}
                error={validationErrors.firstName}
                helperText={validationErrors.firstName}
                className={input}
              />

              <TextField
                fullWidth
                variant="outlined"
                label="Last Name"
                name="lastName"
                value={lastName}
                onChange={event => {
                  setLastName(event.target.value);
                  setValidationErrors({
                    ...validationErrors,
                    [event.target.name]: false,
                  });
                }}
                error={validationErrors.lastName}
                helperText={validationErrors.lastName}
                className={input}
              />

              <TextField
                fullWidth
                multiline
                variant="outlined"
                rows="4"
                label="Bio"
                name="bio"
                value={bio}
                onChange={event => {
                  setBio(event.target.value);
                  setValidationErrors({
                    ...validationErrors,
                    [event.target.name]: false,
                  });
                }}
                error={validationErrors.bio}
                helperText={validationErrors.bio}
                className={input}
              />

              <TextField
                fullWidth
                variant="outlined"
                label="Title"
                name="title"
                value={title}
                onChange={event => setTitle(event.target.value)}
                className={input}
              />

              <TextField
                fullWidth
                variant="outlined"
                label="Degree"
                name="degree"
                value={degree}
                onChange={event => {
                  setDegree(event.target.value);
                  setValidationErrors({
                    ...validationErrors,
                    [event.target.name]: false,
                  });
                }}
                error={validationErrors.degree}
                helperText={validationErrors.degree}
                className={input}
              />

              <TextField
                fullWidth
                variant="outlined"
                label="Education"
                name="education"
                value={education}
                onChange={event => {
                  setEducation(event.target.value);
                  setValidationErrors({
                    ...validationErrors,
                    [event.target.name]: false,
                  });
                }}
                className={input}
              />
              <TextField
                fullWidth
                variant="outlined"
                label="Email"
                name="email"
                value={email}
                onChange={event => {
                  setEmail(event.target.value);
                  setValidationErrors({
                    ...validationErrors,
                    [event.target.name]: false,
                  });
                }}
                error={validationErrors.email}
                helperText={validationErrors.email}
                className={input}
                inputProps={{
                  onBlur: async () => {
                    const available = await isEmailAvailable(email);
                    if (!available) {
                      setValidationErrors({
                        ...validationErrors,
                        email: 'This email is already in use',
                      });
                    }
                  },
                }}
              />
            <FormControl
              component="fieldset"
              className={input}
              error={!!validationErrors.notificationPreference}
            >
              <FormLabel component="legend">Notification Preference</FormLabel>
              <RadioGroup
                row
                name="notificationPreference"
                value={notificationPreference}
                onChange={e => {
                  setNotificationPreference(e.target.value);
                  setValidationErrors({
                    ...validationErrors,
                    notificationPreference: false,
                  });
                }}
              >
                <FormControlLabel value="both" control={<Radio />} label="Both" />
                <FormControlLabel value="email" control={<Radio />} label="Email" />
                <FormControlLabel value="fax" control={<Radio />} label="Fax" />
              </RadioGroup>
              {validationErrors.notificationPreference && (
                <FormHelperText>
                  {validationErrors.notificationPreference}
                </FormHelperText>
              )}
               <FormHelperText>
                {notificationPreference === "email" && "You will receive all notifications by email only."}
                {notificationPreference === "both" && "You will receive notifications by both email and fax."}
                {notificationPreference === "fax" && "You will receive all notifications by fax only. Fax number required below."}
              </FormHelperText>
            </FormControl>
            <TextField
                fullWidth
                variant="outlined"
                label="Fax"
                name="fax"
                value={fax ? `+1 ${formatFax(fax)}` : "+1 "}
                onChange={(event) => {
                  const input = event.target.value;
                  // Extract digits and remove country code if user types it
                  const digits = input.replace(/\D/g, "");
                  const withoutCountry = digits.startsWith("1") ? digits.slice(1) : digits;
                  const limitedDigits = withoutCountry.slice(0, 10); // ensure max 10 digits

                  setFax(limitedDigits);

                  // Validation logic
                  if (limitedDigits.length > 0 && limitedDigits.length < 10) {
                    setValidationErrors({
                      ...validationErrors,
                      fax: "Fax must be 10 digits",
                    });
                  } else {
                    setValidationErrors({
                      ...validationErrors,
                      fax: false,
                    });
                  }
                }}
                error={!!validationErrors.fax}
                helperText={
                  validationErrors.fax ||
                  "Provide a 10-digit fax number. All faxes will be sent via Metrofax."
                }
              />

              <TextField
                fullWidth
                variant="outlined"
                label="Phone for patients to reach you"
                name="phone"
                value={phone}
                onChange={event => {
                  setPhone(event.target.value);
                  setValidationErrors({
                    ...validationErrors,
                    [event.target.name]: false,
                  });
                }}
                error={validationErrors.phone}
                helperText={validationErrors.phone}
                className={input}
                InputProps={{
                  inputComponent: PhoneInput,
                }}
              />

              <TextField
                fullWidth
                variant="outlined"
                label="Profile URL"
                name="profileUrl"
                value={profileUrl}
                onChange={event => {
                  setProfileUrl(event.target.value);
                  setValidationErrors({
                    ...validationErrors,
                    [event.target.name]: false,
                  });
                }}
                className={input}
              />

              <TextField
                fullWidth
                variant="outlined"
                label="Homepage URL"
                name="homepageUrl"
                value={homepageUrl}
                onChange={event => {
                  setHomepageUrl(event.target.value);
                  setValidationErrors({
                    ...validationErrors,
                    [event.target.name]: false,
                  });
                }}
                className={input}
              />
              <FormControl
                component="fieldset"
                variant="filled"
                className={input}
              >
                <FormLabel component="legend">
                  Choose Preferred Patient Age for booking appointment
                </FormLabel>
                <RadioGroup
                  row
                  aria-label="Preferred Patient Age"
                  name="preferredAge"
                  value={preferredAge}
                  onChange={event => {
                    setPreferredAge(event.target.value);
                    setValidationErrors({
                      ...validationErrors,
                      [event.target.name]: false,
                    });
                  }}
                >
                  <FormControlLabel
                    value="all"
                    control={<Radio />}
                    label="All"
                  />

                  <FormControlLabel
                    value="above_18"
                    control={<Radio />}
                    label="Above 18+"
                  />
                  <FormControlLabel
                    value="below_26"
                    control={<Radio />}
                    label="Below 26+"
                  />
                </RadioGroup>
                {validationErrors.preferredAge && (
                  <FormLabel component="legend" className={error}>
                    {validationErrors.preferredAge}
                  </FormLabel>
                )}
              </FormControl>
              <FormControl
                component="fieldset"
                variant="filled"
                className={input}
              >
                <FormLabel component="legend">
                  Choose Preferred Patient Gender for booking appointment
                </FormLabel>
                <RadioGroup
                  row
                  aria-label="Preferred Patient Gender"
                  name="preferredGender"
                  value={preferredGender}
                  onChange={event => {
                    setPreferredGender(event.target.value);
                    setValidationErrors({
                      ...validationErrors,
                      [event.target.name]: false,
                    });
                  }}
                >
                  <FormControlLabel
                    value="all"
                    control={<Radio />}
                    label="All"
                  />
                  <FormControlLabel
                    value="female"
                    control={<Radio />}
                    label="Female"
                  />
                  <FormControlLabel
                    value="male"
                    control={<Radio />}
                    label="Male"
                  />
                </RadioGroup>
                {validationErrors.preferredGender && (
                  <FormLabel component="legend" className={error}>
                    {validationErrors.preferredGender}
                  </FormLabel>
                )}
              </FormControl>
              <FormControl fullWidth variant="outlined" className={input}>
                <InputLabel htmlFor="select">Provided Url</InputLabel>
                <Select
                  fullWidth
                  variant="outlined"
                  label="Provided Url"
                  name="providerUrl"
                  value={providerUrl}
                  onChange={event => {
                    setProviderUrl(event.target.value);
                    setValidationErrors({
                      ...validationErrors,
                      [event.target.name]: false,
                    });
                  }}
                  error={validationErrors.providerUrl}
                  helperText={validationErrors.providerUrl}
                  className={input}
                >
                  {allProvidedUrls.map(specialtie => (
                    <MenuItem
                      key={`menu-${specialtie.shortName}`}
                      value={specialtie.shortName}
                    >
                      <ListItemText primary={specialtie.name} />
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText className={error}>
                  {' '}
                  {!providerUrl ? validationErrors.providerUrl : ''}
                </FormHelperText>
              </FormControl>
              <FormControl fullWidth variant="outlined" className={input}>
                <InputLabel htmlFor="select-multiple-specialties">
                  Specialties
                </InputLabel>
                <Select
                  multiple
                  name="providerSpecialtyIds"
                  value={providerSpecialtyIds}
                  onChange={event =>
                    setProviderSpecialtyIds([...event.target.value])
                  }
                  input={
                    <OutlinedInput
                      id="select-multiple-specialties"
                      labelWidth={80}
                    />
                  }
                  renderValue={specialtyIds =>
                    specialtyIds.map(specialtyId => (
                      <Chip
                        key={`chip-${specialtyId}`}
                        label={
                          (
                            specialties.find(
                              specialty => specialty.id === specialtyId
                            ) || {}
                          ).name
                        }
                      />
                    ))
                  }
                >
                  {specialties.map(specialty => (
                    <MenuItem key={`menu-${specialty.id}`} value={specialty.id}>
                      <Checkbox
                        checked={providerSpecialtyIds.includes(specialty.id)}
                      />
                      <ListItemText primary={specialty.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth variant="outlined" className={input}>
                <InputLabel htmlFor="select-multiple-insurances">
                  Insurances
                </InputLabel>
                <Select
                  multiple
                  name="providerInsuranceIds"
                  value={providerInsuranceIds}
                  onChange={event =>
                    setProviderInsuranceIds([...event.target.value])
                  }
                  input={
                    <OutlinedInput
                      id="select-multiple-insurances"
                      labelWidth={80}
                    />
                  }
                  renderValue={insuranceIds =>
                    insuranceIds.map(insuranceId => (
                      <Chip
                        key={`chip-${insuranceId}`}
                        label={
                          (
                            insurances.find(
                              insurance => insurance.id === insuranceId
                            ) || {}
                          ).name
                        }
                      />
                    ))
                  }
                >
                  {insurances.map(insurance => (
                    <MenuItem key={`menu-${insurance.id}`} value={insurance.id}>
                      <Checkbox
                        checked={providerInsuranceIds.includes(insurance.id)}
                      />
                      <ListItemText primary={insurance.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {admin && (
                <FormControl fullWidth variant="outlined" className={input}>
                  <InputLabel htmlFor="select">Practice</InputLabel>
                  <Select
                    fullWidth
                    variant="outlined"
                    label="Practice"
                    name="practiceId"
                    value={practiceId}
                    onChange={event => {
                      setPracticeId(event.target.value);
                      setValidationErrors({
                        ...validationErrors,
                        [event.target.name]: false,
                      });
                    }}
                    error={validationErrors.practiceId}
                    helperText={validationErrors.practiceId}
                    className={input}
                  >
                    {practices.map(practice => (
                      <MenuItem key={`menu-${practice.id}`} value={practice.id}>
                        <ListItemText primary={practice.name} />
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText className={error}>
                    {' '}
                    {!practiceId ? validationErrors.practiceId : ''}
                  </FormHelperText>
                </FormControl>
              )}

              <Grid justifyContent="space-between">
                <Grid container>
                  <div style={{ display: 'contents' }}>
                    Opt into SMS notifications
                  </div>
                  <Grid container>
                    <Checkbox
                      checked={smsOptIn}
                      onChange={() => {
                        setSmsOptIn(!smsOptIn);
                        setValidationErrors({
                          ...validationErrors,
                          smsPhone: false,
                        });
                      }}
                    />
                    <Grid xs="8" className="smallTxt">
                      <TextField
                        disabled={!smsOptIn}
                        fullWidth
                        label="phone to receive Qwell notifications to"
                        name="smsPhone"
                        value={smsPhone}
                        onChange={event => {
                          setSmsPhone(event.target.value);
                          setValidationErrors({
                            ...validationErrors,
                            [event.target.name]: false,
                          });
                        }}
                        error={validationErrors.smsPhone}
                        helperText={validationErrors.smsPhone}
                        className={input}
                        InputProps={{
                          inputComponent: PhoneInput,
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <div className="actionBtn">
                  <Grid className={actions}>
                    <Button
                      color="primary"
                      variant="contained"
                      onClick={submitSave}
                      onClickValidation={validateProvider}
                      disabled={buttonDisable}
                    >
                      Save Profile
                    </Button>
                  </Grid>
                </div>
              </Grid>
            </TabPanel>
          </div>
        )}

        {tabPosition === 1 && (
          <TabPanel>
            <Grid container>
              {tenancies.map((tenancy, idx) => (
                <Paper key={tenancy.id || `tenancy-${idx}`}>
                  <Grid item xs={12} className="paddingZero">
                    <header>
                      <h4>{tenancy.location}</h4>
                    </header>

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={tenancy.active}
                          onChange={() =>
                            setTenancies(
                              tenancies.map((tenancy, i) => {
                                if (i === idx)
                                  return {
                                    ...tenancy,
                                    active: !tenancy.active,
                                  };
                                return tenancy;
                              })
                            )
                          }
                          value={'active'}
                        />
                      }
                      label="Active"
                      classes={{ root: input }}
                    />

                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Address"
                      name="location"
                      value={tenancy.location}
                      onChange={onTenancyFieldChange(idx)}
                      classes={{ root: input }}
                    />

                    <TextField
                      fullWidth
                      variant="outlined"
                      label="City/Short Name"
                      name="locationShortName"
                      value={tenancy.locationShortName}
                      onChange={onTenancyFieldChange(idx)}
                      classes={{ root: input }}
                    />

                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Zipcode"
                      name="zipcode"
                      value={tenancy.zipcode}
                      onChange={onTenancyFieldChange(idx)}
                      classes={{ root: input }}
                    />

                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Color"
                      name="color"
                      value={tenancy.color}
                      onChange={onTenancyFieldChange(idx)}
                      classes={{ root: input }}
                    />

                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Appointment Length (in minutes)"
                      name="apptDurationMinutes"
                      value={tenancy.apptDurationMinutes}
                      onChange={onTenancyFieldChange(idx)}
                      classes={{ root: input }}
                    />
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Telmedicine Virtual Room Link"
                      name="virtualRoomUrl"
                      value={tenancy.virtualRoomUrl}
                      onChange={onTenancyFieldChange(idx)}
                      classes={{ root: input }}
                    />
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Google Review Link"
                      name="googleReviewLink"
                      value={tenancy.googleReviewLink}
                      onChange={onTenancyFieldChange(idx)}
                      classes={{ root: input }}
                    />
                  </Grid>
                </Paper>
              ))}

              <div className="addSaveTenancy">
                <Grid item xs={6} classes={{ item: actions }}>
                  <Grid container justifyContent="flex-start">
                    <Grid item xs={12} lg={4}>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={onNewTenancy}
                      >
                        + Add Tenancy
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={6} classes={{ item: actions }}>
                  <Grid container justifyContent="flex-start">
                    <Grid item xs={12} lg={4}>
                      <Button
                        fullWidth
                        color="primary"
                        variant="contained"
                        onClick={() => saveTenancies(tenancies)}
                      >
                        Save Tenancies
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </div>
            </Grid>
          </TabPanel>
        )}
      </DialogContent>
    </Dialog>
  );
}

const styles = () => ({
  modal: {
    maxHeight: '700px',
  },
  modalContent: {
    overflow: 'visible',
  },
  actions: {
    marginTop: '2em',
    float: 'left',
  },
  tenancy: {
    padding: '1em',
    margin: '1em 0',
  },
  input: {
    margin: '0.5em 0',
  },
  error: {
    color: '#D8000C',
  },
  addSaveTenancy: {
    display: 'flex',
    justifyContent: 'space-between',
  },
});

ProviderEditModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  provider: PropTypes.object,
  saveTenancies: PropTypes.func.isRequired,
  saveProfile: PropTypes.func.isRequired,
  admin: PropTypes.bool,
};

export default withStyles(styles)(ProviderEditModal);
